<script setup lang="ts">
import { ref, type Ref, watch, onUnmounted } from 'vue';
import CColorPicker from '@/components/shared/CColorPicker';
import CButton from '@/components/shared/CButton';
import { type FullColorObject, type PaletteColorObject, type GradientStopObject, type ColorPickerTypeValue, ColorPickerType, useColor, CColorPickerValue } from '@/composables/color';
import type { ColorObject, GradientObject, PaletteObject } from '@/typings/libraries';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';

const props = defineProps<{
    open?: boolean;
    color?: FontColorOption;
    isCmyk?: boolean;
    enablePalette?: boolean;
    hideGradient?: boolean;
    showMenuTypes?: boolean;
}>();

const emit = defineEmits<{
    (e: 'color-selected', color: FontColorOption): void;
    (e: 'color-changed', color: FontColorOption): void;
    (e: 'cancel'): void;
}>();

const colord = useColor();
const currentColor: Ref<CColorPickerValue | undefined> = ref();
const currentType: Ref<ColorPickerTypeValue> = ref(ColorPickerType.SOLID);

const createColorWithCmyk = (colorItem: { hex: string; alpha: number }) => {
    const color = colord.getColorObj({ hex: colorItem.hex, a: colorItem.alpha });
    return {
        ...colorItem,
        cmyk: color?.cmyk || { c: 0, m: 0, y: 0, k: 0 }
    };
};

// Same source as a solid color: in CMYK mode the stored CMYK is the one the user typed, otherwise it comes from the hex
const createPaletteColorWithCmyk = (colorItem: ColorObject) => {
    if (props.isCmyk && typeof colorItem.cmyk === 'string') {
        return { ...colorItem, cmyk: colord.cmykStringToObject(colorItem.cmyk) };
    }
    return createColorWithCmyk(colorItem);
};

const createColorWithCmykString = (v: GradientStopObject | PaletteColorObject) => {
    return {
        ...v,
        cmyk: v.cmyk ? colord.cmykToDecimalString(v.cmyk) : undefined
    }
};

const getFontColorValue = (color: CColorPickerValue): FontColorOption => {
    let value: FontColorOption;

    if ('colorStops' in color) {
        const colorStops = color.colorStops.map(createColorWithCmykString);
        value = {
            ...color,
            colorStops
        } as GradientObject;
    } else if ('colors' in color) {
        const colors = color.colors.map(createColorWithCmykString);
        value = {
            ...color,
            colors
        } as PaletteObject;
    } else {
        value = {
            hex: color.hex,
            alpha: color.a,
            cmyk: colord.cmykToDecimalString(color.cmyk)
        } as ColorObject;
    }

    return value;
}

const onColorSelected = (preview?: boolean) => {
    if (!currentColor.value) return;

    const color = getFontColorValue(currentColor.value);

    if (preview) {
        emit('color-changed', color);
        return;
    }

    emit('color-selected', color);
};

const onClearSelectedColor = () => {
    currentColor.value = undefined;
    currentType.value = ColorPickerType.SOLID;
};

watch(() => props.color, (val) => {
    if (!val) {
        onClearSelectedColor();
        return;
    }

    if ('colorStops' in val) {
        const stops = val.colorStops.map(createColorWithCmyk) as GradientStopObject[];
        currentColor.value = { ...val, colorStops: stops };
        currentType.value = ColorPickerType.GRADIENT;
    } else if ('colors' in val) {
        const colors = val.colors.map(createPaletteColorWithCmyk) as PaletteColorObject[];
        currentColor.value = { ...val, colors };
        currentType.value = ColorPickerType.PALETTE;
    } else {
        const color = val as ColorObject;
        const value: Partial<FullColorObject> = props.isCmyk ? { cmyk: colord.cmykStringToObject(color.cmyk) } : { hex: color.hex };
        currentColor.value = colord.getColorObj({ ...value, a: (val as ColorObject).alpha });
        currentType.value = ColorPickerType.SOLID;
    }
}, { immediate: true });

onUnmounted(() => {
    onClearSelectedColor();
});
</script>

<template>
    <div class="color-selector-wrapper">
        <c-color-picker
            v-if="open"
            v-model="currentColor"
            class="color-picker"
            :mode="isCmyk ? 'cmyk' : 'rgb'"
            :allow-reset="false"
            :show-input-menu="showMenuTypes"
            :hide-palette="!enablePalette"
            :hide-gradient="hideGradient"
            :picker-type="currentType"
            @input="onColorSelected(true)"
        >
            <template #footer>
                <div class="d-flex justify-content-end mt-auto">
                    <c-button class="mr-2" variant="outline-primary" size="sm" text="CANCEL" @click="emit('cancel')" />
                    <c-button variant="primary" size="sm" text="APPLY" @click="onColorSelected()" />
                </div>
            </template>
        </c-color-picker>
    </div>
</template>

<style lang="scss" scoped>
.color-selector-wrapper {
    position: relative;
    width: 28rem;
    z-index: 9999;
    .color-picker {
        position: absolute;
        width: 100%;
        bottom: 100%;
        top: auto;
    }
}
</style>
